import type { AccountHistoryPoint, GrowthPoint } from "../types";

type ValueKey = "account_value" | "margin_balance";
const DAY = 86_400_000;

/** Restrict independently rebased performance series to their overlapping date span and
    rebase both at the later fund's start (for example, NTSX's 2018 inception). */
export function alignGrowthSeries(
  a: GrowthPoint[],
  b: GrowthPoint[],
): [GrowthPoint[], GrowthPoint[]] {
  if (a.length === 0 || b.length === 0) return [a, b];
  const start = [a[0].date, b[0].date].sort().at(-1)!;
  const end = [a.at(-1)!.date, b.at(-1)!.date].sort()[0];
  const rebase = (values: GrowthPoint[]) => {
    const atBoundary = (date: string): GrowthPoint => {
      const nextIndex = values.findIndex((point) => point.date >= date);
      const next = values[nextIndex < 0 ? values.length - 1 : nextIndex];
      if (next.date === date || nextIndex <= 0) return { date, pct: next.pct };
      const previous = values[nextIndex - 1];
      const previousTime = new Date(previous.date).getTime();
      const nextTime = new Date(next.date).getTime();
      const fraction = (new Date(date).getTime() - previousTime) / (nextTime - previousTime);
      return { date, pct: previous.pct + (next.pct - previous.pct) * fraction };
    };
    const middle = values.filter((point) => point.date > start && point.date < end);
    const shared = start === end
      ? [atBoundary(start)]
      : [atBoundary(start), ...middle, atBoundary(end)];
    if (shared.length === 0) return shared;
    const base = 1 + shared[0].pct / 100;
    if (base <= 0) return shared;
    return shared.map((point) => ({
      ...point,
      pct: ((1 + point.pct / 100) / base - 1) * 100,
    }));
  };
  return [rebase(a), rebase(b)];
}

export function annualGrowthRate(
  points: AccountHistoryPoint[],
  key: ValueKey,
): number | null {
  const values = points
    .filter((point) => point[key] != null)
    .map((point) => ({ time: new Date(point.observed_at).getTime(), value: Number(point[key]) }))
    .filter((point) => Number.isFinite(point.time) && Number.isFinite(point.value));
  if (values.length < 2) return null;
  const first = values[0];
  const last = values.at(-1)!;
  const years = (last.time - first.time) / (365.2425 * DAY);
  if (years <= 0 || first.value <= 0 || last.value < 0) return null;
  return ((last.value / first.value) ** (1 / years) - 1) * 100;
}
